import React, { useState } from 'react';
import styled from 'styled-components';
import { useToastContext } from '../../context/ToastContext';

interface PromoCodeInputProps {
  onPromoCodeApplied: (promoCode: { codigo: string; porcentajeDescuento: number }) => void;
  disabled?: boolean;
}

const PromoContainer = styled.div`
  background-color: ${props => props.theme.colors.background.tertiary};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.large};
  margin-bottom: ${props => props.theme.spacing.medium};
  border: 1px solid ${props => props.theme.colors.border.primary};
`;

const PromoTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: ${props => props.theme.spacing.medium};
  color: ${props => props.theme.colors.text.primary};
  font-weight: 600;
`;

const PromoForm = styled.form`
  display: flex;
  gap: ${props => props.theme.spacing.small};
  align-items: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PromoInputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xsmall};
`;

const PromoLabel = styled.label`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 500;
`;

const PromoInput = styled.input<{ hasError?: boolean }>`
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  border: 1px solid ${props => props.hasError ? '#ff4444' : props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 1rem;
  background-color: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};
  text-transform: uppercase;
  transition: all ${props => props.theme.transitions.medium};
  
  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
    opacity: 0.7;
    text-transform: none;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.text.accent};
    box-shadow: 0 0 0 2px rgba(74, 107, 175, 0.3);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background.tertiary};
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PromoButton = styled.button<{ isLoading?: boolean }>`
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.large};
  background-color: ${props => props.theme.colors.button.primary.background};
  color: ${props => props.theme.colors.button.primary.text};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.medium};
  white-space: nowrap;
  min-width: 100px;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.button.primary.hover};
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background.tertiary};
    color: ${props => props.theme.colors.text.disabled};
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    margin-top: ${props => props.theme.spacing.small};
  }
`;

const AppliedPromoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid #4caf50;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.medium};
  margin-top: ${props => props.theme.spacing.small};
`;

const AppliedPromoText = styled.span`
  color: #4caf50;
  font-weight: 600;
  font-size: 0.9rem;
`;

const RemovePromoButton = styled.button`
  background: none;
  border: none;
  color: #4caf50;
  cursor: pointer;
  font-size: 1.2rem;
  padding: ${props => props.theme.spacing.xsmall};
  border-radius: 50%;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: rgba(76, 175, 80, 0.1);
    transform: scale(1.1);
  }
`;

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({ onPromoCodeApplied, disabled = false }) => {
  const [promoCode, setPromoCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{ codigo: string; porcentajeDescuento: number } | null>(null);
  const [hasError, setHasError] = useState(false);
  const { showSuccess, showError } = useToastContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!promoCode.trim() || isLoading || disabled) return;
    
    setIsLoading(true);
    setHasError(false);
    
    try {
      const response = await fetch('https://canvasoft.com:8487/canvamanager/descuentos/validar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ codigo: promoCode.trim().toUpperCase() }),
      });
      
      const data = await response.json();
      
      if (data.valid) {
        const promoData = {
          codigo: data.codigo,
          porcentajeDescuento: data.porcentajeDescuento
        };
        
        setAppliedPromo(promoData);
        onPromoCodeApplied(promoData);
        setPromoCode('');
        showSuccess(`¡Código promocional aplicado! ${data.porcentajeDescuento}% de descuento`);
      } else {
        setHasError(true);
        showError(data.message || 'Código promocional inválido o expirado');
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setHasError(true);
      showError('Error al validar el código promocional. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    onPromoCodeApplied({ codigo: '', porcentajeDescuento: 0 });
    showSuccess('Código promocional removido');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value);
    if (hasError) {
      setHasError(false);
    }
  };

  return (
    <PromoContainer>
      <PromoTitle>¿Tienes un código promocional?</PromoTitle>
      
      {!appliedPromo ? (
        <PromoForm onSubmit={handleSubmit}>
          <PromoInputContainer>
            <PromoLabel>Código promocional</PromoLabel>
            <PromoInput
              type="text"
              value={promoCode}
              onChange={handleInputChange}
              placeholder=""
              disabled={disabled || isLoading}
              hasError={hasError}
              maxLength={50}
            />
          </PromoInputContainer>
          <PromoButton 
            type="submit" 
            disabled={!promoCode.trim() || isLoading || disabled}
            isLoading={isLoading}
          >
            {isLoading ? 'Validando...' : 'Aplicar'}
          </PromoButton>
        </PromoForm>
      ) : (
        <AppliedPromoContainer>
          <AppliedPromoText>
            Código aplicado: {appliedPromo.codigo} ({appliedPromo.porcentajeDescuento}% descuento)
          </AppliedPromoText>
          <RemovePromoButton onClick={handleRemovePromo} title="Remover código promocional">
            ×
          </RemovePromoButton>
        </AppliedPromoContainer>
      )}
    </PromoContainer>
  );
};

export default PromoCodeInput;